/**
 * Only use this prop converter for deprecated-react-charts charts. For the new charting library's charts,
 * use the converters in prop-converters or create a new one.
 */
import {DataType, DataTypeToTSType} from 'data-frames/DataFrameTypes';
import {
  BaseChartParameters,
  VisualizationType,
} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import runQueryOutputToChartData, {
  ColumnInfo,
} from 'insights/analytics-visualizations/viewers/prop-converters/legacy-prop-converters/runQueryOutputToChartData';
import {RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {JSONValue} from 'util/Types';

import ConvertToChartPropsException from '../ConvertToChartPropsException';

export type PossibleXTypes = number | string;
export type XAxisCompatibleDataType = DataType.DATE_OR_TIMESTAMP | DataType.NUMBER | DataType.STRING;

const MAXIMUM_SERIES = 100;
export const TOO_MANY_SERIES_ERROR = `Chart cannot render more than ${MAXIMUM_SERIES} different series`;

type BaseChartData<XType> = Array<{x: XType; y: number}>;

export interface BaseChartDataset<XType> {
  data: BaseChartData<XType>;
  label?: string;
}

interface BaseChartProps<XType> {
  datasets: Array<BaseChartDataset<XType>>;
  xAxisLabel: string;
  yAxisLabel: string;
}

// Only use these for the react-charts components. For the new ui-library/charting components, use their
// compatibility definitions.
interface LegacyCompatibleColumnTypes {
  x: DataType.NUMBER | DataType.DATE_OR_TIMESTAMP | DataType.STRING;
  y: DataType.NUMBER;
}
type SerialLegacyCompatibleColumnTypes = LegacyCompatibleColumnTypes & {
  series: DataType.NUMBER | DataType.STRING;
};

type ConversionOutput = {
  [k in XAxisCompatibleDataType]: {props: BaseChartProps<DataTypeToTSType[k]>; xAxisType: k};
}[XAxisCompatibleDataType];

export default function convertToBaseChartProps(
  runQueryOutput: RunQueryOutputLegacyData,
  {seriesColumnName, xAxisColumnName, yAxisColumnName}: BaseChartParameters,
  possibleXAxisTypes: Array<XAxisCompatibleDataType>,
  visualizationType: VisualizationType
): ConversionOutput {
  let columnInfos: Array<ColumnInfo<{x: JSONValue; y: JSONValue; series?: JSONValue}, 'series'>> = [
    {
      chartDataColumnName: 'x',
      runQueryColumnName: xAxisColumnName,
      possibleTypes: possibleXAxisTypes,
      filterNulls: true,
    },
    {
      chartDataColumnName: 'y',
      runQueryColumnName: yAxisColumnName,
      possibleTypes: [DataType.NUMBER],
      filterNulls: true,
    },
  ];
  if (seriesColumnName != null) {
    columnInfos = [
      ...columnInfos,
      {
        chartDataColumnName: 'series',
        runQueryColumnName: seriesColumnName,
        possibleTypes: [DataType.NUMBER, DataType.STRING],
        filterNulls: true,
        isSeriesLabel: true,
      },
    ];
  }
  // 'series' is included in the typing, but the output is only affected if there is a series column in columnInfos
  const finalData = runQueryOutputToChartData<
    {x: JSONValue; y: JSONValue; series?: JSONValue},
    SerialLegacyCompatibleColumnTypes,
    'series'
  >(runQueryOutput, columnInfos, visualizationType);

  if (finalData.datasets.length > MAXIMUM_SERIES) {
    throw new ConvertToChartPropsException(TOO_MANY_SERIES_ERROR);
  }

  const outputDatasets = (
    finalData.datasets as Array<{
      points: Array<{x: JSONValue; y: JSONValue}>;
      label?: number | string;
    }>
  ).map((dataset) => ({
    data: dataset.points,
    // Chart.js types require string labels, so convert to string here
    ...(dataset.label == null ? {} : {label: dataset.label.toString()}),
  }));

  return {
    props: {
      datasets: outputDatasets,
      xAxisLabel: xAxisColumnName,
      yAxisLabel: yAxisColumnName,
    },
    valid: true,
    xAxisType: finalData.axisTypes.x,
  } as ConversionOutput;
}
